import { useMemo } from 'react';
import { ListBuilderFilters, ListBuilderFilterOptions } from '@/hooks/useListBuilder';
import { AccountFilterSidebar, FilterSection } from '@/components/accounts/AccountFilterSidebar';
import {
  ACCOUNT_TIER_ORDER,
  ACCOUNT_TIER_LABELS,
  ICP_TIER_ORDER,
  CLINICAL_PHASE_ORDER,
  CLINICAL_PHASE_LABELS,
  EMPLOYEE_RANGE_ORDER,
  STANDARD_SENIORITY_LEVELS,
  STANDARD_DEPARTMENTS,
} from '@/config/filters';

interface ListBuilderFilterPanelProps {
  filters: ListBuilderFilters;
  onFiltersChange: (filters: ListBuilderFilters) => void;
  filterOptions: ListBuilderFilterOptions | undefined;
  totalCount: number;
}

export function ListBuilderFilterPanel({
  filters,
  onFiltersChange,
  filterOptions,
  totalCount,
}: ListBuilderFilterPanelProps) {

  // ── Map flat filters ↔ sidebar's Record<string, string[]> ────────

  const filterValues: Record<string, string[]> = useMemo(() => ({
    salesReps: filters.salesReps || [],
    accountTiers: filters.accountTiers || [],
    icpTiers: filters.icpTiers || [],
    territories: filters.territories || [],
    carrModalities: filters.carrModalities || [],
    modalities: filters.modalities || [],
    therapeuticAreas: filters.therapeuticAreas || [],
    clinicalPhases: filters.clinicalPhases || [],
    fundingStatuses: filters.fundingStatuses || [],
    employeeRanges: filters.employeeRanges || [],
    companyCountries: filters.companyCountries || [],
    seniorityLevels: filters.seniorityLevels || [],
    departments: filters.departments || [],
    buyerPersonas: filters.buyerPersonas || [],
    marketingPersonas: filters.marketingPersonas || [],
    outreachStatuses: filters.outreachStatuses || [],
    emailStatuses: filters.emailStatuses || [],
    contactTags: filters.contactTags || [],
    campaignStatuses: filters.campaignStatuses || [],
  }), [filters]);

  const toggleValues: Record<string, boolean> = useMemo(() => ({
    topAccountsOnly: filters.topAccountsOnly || false,
    decisionMakersOnly: filters.decisionMakersOnly || false,
    hasEmail: filters.hasEmail || false,
    emailVerifiedOnly: filters.emailVerifiedOnly || false,
    hasLinkedIn: filters.hasLinkedIn || false,
  }), [filters]);

  const handleFilterChange = (sectionId: string, values: string[]) => {
    onFiltersChange({
      ...filters,
      [sectionId]: values.length > 0 ? values : undefined,
      offset: 0,
    });
  };

  const handleToggleChange = (key: string, value: boolean) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
      offset: 0,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({ limit: filters.limit, offset: 0 });
  };

  const hasActiveFilters = !!(
    filters.searchTerm || filters.seniorityLevels?.length || filters.departments?.length ||
    filters.buyerPersonas?.length || filters.marketingPersonas?.length ||
    filters.hasEmail || filters.hasLinkedIn ||
    filters.emailVerifiedOnly || filters.emailStatuses?.length || filters.decisionMakersOnly ||
    filters.outreachStatuses?.length ||
    filters.companyCountries?.length || filters.topAccountsOnly ||
    filters.carrModalities?.length || filters.modalities?.length ||
    filters.employeeRanges?.length || filters.fundingStatuses?.length ||
    filters.salesReps?.length || filters.accountTiers?.length || filters.icpTiers?.length ||
    filters.clinicalPhases?.length || filters.therapeuticAreas?.length ||
    filters.territories?.length || filters.contactTags?.length ||
    filters.campaignStatuses?.length || filters.lastCampaignAfter || filters.lastCampaignBefore
  );

  // ── Build filter sections ─────────────────────────────────────────

  const sections: FilterSection[] = useMemo(() => {
    const sortedAccountTiers = ACCOUNT_TIER_ORDER;
    const sortedIcpTiers = (filterOptions?.icpTiers || []).sort(
      (a, b) => ICP_TIER_ORDER.indexOf(a) - ICP_TIER_ORDER.indexOf(b)
    );
    const sortedEmployeeRanges = EMPLOYEE_RANGE_ORDER;
    const sortedClinicalPhases = CLINICAL_PHASE_ORDER.filter(
      (p) => (filterOptions?.clinicalPhases || []).includes(p)
    );
    const seniorityOptions = filterOptions?.seniorityLevels?.length
      ? filterOptions.seniorityLevels : STANDARD_SENIORITY_LEVELS;
    const departmentOptions = filterOptions?.departments?.length
      ? filterOptions.departments : STANDARD_DEPARTMENTS;

    const s: FilterSection[] = [
      // ═══ Territory & Scope ═══
      { id: '_divider_scope', label: 'Territory & Scope', type: 'divider' as const, options: [], groupLabel: 'Territory & Scope' },
      // 1. Sales Rep
      ...(filterOptions?.salesReps?.length ? [{
        id: 'salesReps',
        label: 'Sales Rep',
        options: filterOptions.salesReps.map((r) => ({ value: r, label: r })),
      }] : []),
      // 2. Territory
      ...(filterOptions?.territories?.length ? [{
        id: 'territories',
        label: 'Territory',
        options: filterOptions.territories.map((t) => ({ value: t, label: t })),
      }] : []),
      // 3. ICP Tier
      ...(sortedIcpTiers.length ? [{
        id: 'icpTiers',
        label: 'ICP Tier',
        options: sortedIcpTiers.map((t) => ({ value: t, label: t })),
      }] : []),

      // ═══ Account Filters ═══
      { id: '_divider_account', label: 'Account Filters', type: 'divider' as const, options: [], groupLabel: 'Account Filters' },
      // 4. Account Status
      {
        id: 'accountTiers',
        label: 'Account Status',
        options: sortedAccountTiers.map((t) => ({
          value: t,
          label: ACCOUNT_TIER_LABELS[t] || t,
        })),
      },
      // 5. Modality Type
      ...(filterOptions?.carrModalities?.length ? [{
        id: 'carrModalities',
        label: 'Modality Type',
        options: filterOptions.carrModalities.map((m) => ({ value: m, label: m })),
      }] : []),
      // 6. Cell Type
      ...(filterOptions?.modalities?.length ? [{
        id: 'modalities',
        label: 'Cell Type',
        options: filterOptions.modalities.map((m) => ({ value: m, label: m })),
      }] : []),
      // 7. Therapeutic Area
      ...(filterOptions?.therapeuticAreas?.length ? [{
        id: 'therapeuticAreas',
        label: 'Therapeutic Area',
        options: filterOptions.therapeuticAreas.map((a) => ({ value: a, label: a })),
      }] : []),
      // 8. Clinical Phase
      ...(sortedClinicalPhases.length ? [{
        id: 'clinicalPhases',
        label: 'Clinical Phase',
        options: sortedClinicalPhases.map((p) => ({
          value: p,
          label: CLINICAL_PHASE_LABELS[p] || p,
        })),
      }] : []),
      // 9. Funding Status
      ...(filterOptions?.fundingStatuses?.length ? [{
        id: 'fundingStatuses',
        label: 'Funding Status',
        options: filterOptions.fundingStatuses.map((f) => ({ value: f, label: f })),
      }] : []),
      // 10. Company Size
      {
        id: 'employeeRanges',
        label: 'Company Size',
        options: sortedEmployeeRanges.map((r) => ({ value: r, label: r })),
      },
      // 11. Country
      ...(filterOptions?.companyCountries?.length ? [{
        id: 'companyCountries',
        label: 'Country',
        options: filterOptions.companyCountries.map((c) => ({ value: c, label: c })),
      }] : []),
      // 12. Top Accounts
      {
        id: 'topAccountsOnly',
        label: 'Top Accounts',
        type: 'toggle' as const,
        options: [],
        toggleKey: 'topAccountsOnly',
        toggleLabel: '⭐ Top Accounts Only',
      },

      // ═══ Contact Filters ═══
      { id: '_divider_contact', label: 'Contact Filters', type: 'divider' as const, options: [], groupLabel: 'Contact Filters' },
      // 13. Seniority Level
      {
        id: 'seniorityLevels',
        label: 'Seniority Level',
        options: seniorityOptions.map((l) => ({ value: l, label: l })),
      },
      // 14. Department
      {
        id: 'departments',
        label: 'Department',
        options: departmentOptions.map((d) => ({ value: d, label: d })),
      },
      // 15. Buyer Persona
      ...(filterOptions?.buyerPersonas?.length ? [{
        id: 'buyerPersonas',
        label: 'Buyer Persona',
        options: filterOptions.buyerPersonas.map((p) => ({ value: p, label: p })),
      }] : []),
      // 16. Marketing Persona
      ...(filterOptions?.marketingPersonas?.length ? [{
        id: 'marketingPersonas',
        label: 'Marketing Persona',
        options: filterOptions.marketingPersonas.map((p) => ({ value: p, label: p })),
      }] : []),
      // 17. Decision Makers
      {
        id: 'decisionMakersOnly',
        label: 'Decision Makers',
        type: 'toggle' as const,
        options: [],
        toggleKey: 'decisionMakersOnly',
        toggleLabel: 'Decision Makers Only',
      },
      // 18. Outreach Status
      ...(filterOptions?.outreachStatuses?.length ? [{
        id: 'outreachStatuses',
        label: 'Outreach Status',
        options: filterOptions.outreachStatuses.map((s) => ({ value: s, label: s })),
      }] : []),
      // 19. Campaign History
      {
        id: 'campaignStatuses',
        label: 'Campaign History',
        options: [
          { value: 'in_active', label: 'In Active Campaign' },
          { value: 'in_archived', label: 'In Archived Campaign' },
          { value: 'never', label: 'Never in Campaign' },
        ],
      },
      // 20. Operational Filters (group)
      {
        id: 'emailStatuses',
        label: 'Operational Filters',
        type: 'group' as const,
        options: (filterOptions?.emailStatuses || []).map((s) => ({ value: s, label: s })),
        toggles: [
          { key: 'hasEmail', label: 'Has Email' },
          { key: 'emailVerifiedOnly', label: 'Verified Email Only' },
          { key: 'hasLinkedIn', label: 'Has LinkedIn' },
        ],
      },
    ];

    return s;
  }, [filterOptions]);

  return (
    <AccountFilterSidebar
      totalCount={totalCount}
      countLabel="contacts"
      searchPlaceholder="Search name, title, company..."
      searchValue={filters.searchTerm || ''}
      onSearchChange={(val) =>
        onFiltersChange({ ...filters, searchTerm: val || undefined, offset: 0 })
      }
      filterSections={sections}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      toggleValues={toggleValues}
      onToggleChange={handleToggleChange}
      onClearFilters={clearAllFilters}
      hasActiveFilters={hasActiveFilters}
      defaultOpenSections={['salesReps']}
      variant="card"
    />
  );
}
